import { useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  alpha,
  styled,
  Button,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { P } from '../theme/palette';
import { Stack } from '@mui/system';
import { useTenantConfig } from '../hooks/useTenant';

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 50,
  background: 'rgba(8, 145, 178, 0.08)',
  border: '1px solid rgba(8, 145, 178, 0.15)',
  marginBottom: 20,
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const AI_FEATURES = [
  'Yazım ve imla hatalarını düzeltir',
  'Metni kurumsal bir dile dönüştürür',
  'Anlatımı güçlendirir',
  'Resmi yazışma formatına uygun hale getirir',
  'Yazıyı mevzuata uygun bir yapıya dönüştürür',
];

const EDITOR_FEATURES = [
  'Gelişmiş metin düzenleme araçları',
  'Tablo, liste ve görsel ekleme',
  'Belge önizleme ve hızlı düzenleme',
  'Kurumsal yazışma formatına uygun yapı',
  'Otomatik başlık ve belge düzeni',
];

const TEMPLATE_FEATURES = [
  'Kurumsal yazışma standardı korunur',
  'Yazı hazırlama süresi kısalır',
  'Belgeler profesyonel bir formatta hazırlanır',
];

const INSTITUTION_TYPES = [
  'Şubeli dernekler',
  'Federasyonlar',
  'Vakıflar',
  'Birlikler',
  'Odalar',
];

const ARCHIVE_FEATURES = [
  'Gelen ve giden evrak yönetimi',
  'Otomatik belge numaralandırma',
  'Gelişmiş belge arama ve filtreleme',
  'Kullanıcı yetkilendirme ve erişim kontrolü',
  'Zaman damgası ile hukuki nitelik, işlem geçmişi ve kayıt sistemi',
  'Güvenli dijital belge arşivi',
];

const EBYSPage = () => {
  const {tenantName} = useTenantConfig();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });


  const ADVANTAGES = [
  {
    icon: '/assets/icon/money.webp',
    title: 'Kağıt ve Baskı Maliyetlerini Ortadan Kaldırır',
    desc: 'Resmi yazışmaların dijital ortamda hazırlanması sayesinde kağıt, toner, yazıcı ve fiziksel arşivleme giderleri ortadan kalkar. Böylece hem çevre korunur hem de dernek bütçesinde önemli tasarruf sağlanır.',
  },
  {
    icon: '/assets/icon/efficiency.webp',
    title: 'Posta ve Kargo Masraflarını Sıfırlar',
    desc: 'Belgelerin gönderimi için kullanılan posta, kargo ve kurye süreçleri tamamen ortadan kalkar. Tüm resmi yazışmalar elektronik ortamda güvenli ve hızlı şekilde gönderilir.',
  },
  {
    icon: '/assets/icon/security.webp',
    title: 'Evrak Kaybolma Riskini Ortadan Kaldırır',
    desc: 'Fiziksel evrak süreçlerinde yaşanan kaybolma, gecikme veya yanlış adrese gönderim gibi riskler tamamen ortadan kalkar. Tüm belgeler güvenli şekilde sistemde saklanır ve gerektiğinde kolayca erişilebilir.',
  },
  {
    icon: '/assets/icon/instant-message.webp',
    title: 'Resmi Kurumlarla Daha Hızlı İletişim',
    desc: `Türkiye’de kamu kurumları resmi yazışmalarını büyük ölçüde elektronik yazışma altyapısı üzerinden yürütmektedir. ${tenantName} EBYS ile yazışmalar çok daha hızlı iletilir ve süreçler önemli ölçüde kısalır.`,
  },
  {
    icon: '/assets/icon/stopwatch.webp',
    title: 'Zaman Tasarrufu ve Operasyonel Verimlilik',
    desc: `${tenantName} EBYS sayesinde belge hazırlama, imzalama, gönderme ve arşivleme işlemleri dijital ortamda dakikalar içinde tamamlanır. Böylece hem zaman tasarrufu sağlanır hem de personelin verimliliği artar.`,
  },
  {
    icon: '/assets/icon/chart.webp',
    title: 'Kendini Kısa Sürede Amorti Eden Sistem',
    desc: `${tenantName} EBYS yalnızca yazışmaları dijitalleştirmekle kalmaz; aynı zamanda kağıt, kargo, arşivleme ve operasyonel iş gücü maliyetlerini önemli ölçüde azaltarak kısa sürede kendini amorti eden bir yapı sunar`,
  },
];

  return (
    <Box ref={ref} sx={{ background: '#ffffff' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, #ecf2ff 0%, #e2e8f0 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Box sx={{ textAlign: 'center', }}>
              <SectionBadge>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#0891b2',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: '#0891b2',
                    letterSpacing: '0.5px',
                  }}
                >
                  EBYS - ELEKTRONİK BELGE YÖNETİM SİSTEMİ
                </Typography>
              </SectionBadge>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  color: '#1e293b',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  mb: 3,
                }}
              >
                Resmi Yazışmalarınızı
                <br />
                <Box component="span" sx={{ color: '#0891b2' }}>
                  Dijitalleştirin
                </Box>
              </Typography>

              <Typography
                sx={{
                  maxWidth: 800,
                  mx: 'auto',
                  color: P.neutral[600],
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                {tenantName} platformunun sahip olduğu BTK lisanslı e-imza altyapısı ve resmi yazışma formatı olan EYP (Elektronik Yazışma Paketi) uyumlu belge üretim sistemi sayesinde tüm resmi kurumlarla yazışmalarınızı kağıt, toner, kargo ve posta giderleri olmadan {tenantName} EBYS ile gerçekleştirebilirsiniz.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 3,
          padding: 7, borderBottom: '1px solid #cbcdcf',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, justifyContent: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 20, color: '#0891b2' }} />
          <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700] }}>
            BTK Lisanslı E-İmza
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, justifyContent: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 20, color: '#0891b2' }} />
          <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700] }}>
            EYP Uyumlu
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, justifyContent: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 20, color: '#0891b2' }} />
          <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700] }}>
            Entegre KEP
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, justifyContent: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 20, color: '#0891b2' }} />
          <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700] }}>
            Yapay Zeka Desteği
          </Typography>
        </Box>
      </Box>

      {/* Advantages Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div
          style={{
            height: '100%'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.8rem' },
                color: '#1e293b',
                mb: 2,
              }}
            >
              {tenantName} EBYS'nin Sağladığı Avantajlar
            </Typography>
            <Typography
              sx={{
                maxWidth: 700,
                mx: 'auto',
                color: P.neutral[600],
                fontSize: '1.05rem',
                lineHeight: 1.7,
              }}
            >
              Kağıt ve baskı maliyetlerinden posta giderlerine, evrak kayıp riskinden
              zaman tasarrufuna kadar birçok avantaj sunar.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {ADVANTAGES.map((item, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <motion.div
                style={{
                  height: '100%'
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    background: '#ffffff',
                    border: `1px solid ${P.neutral[200]}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha('#0891b2', 0.12)}`,
                      borderColor: alpha('#0891b2', 0.3),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)`,
                      boxShadow: `0 8px 20px ${alpha('#0891b2', 0.25)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.title}
                      style={{
                        width: '60%',
                        height: '60%',
                        objectFit: 'contain',
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: P.neutral[800],
                      mb: 1,
                      lineHeight: 1.4,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      color: P.neutral[500],
                      lineHeight: 1.6,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* EYP Section */}
      <Box sx={{ background: '#f8fafc', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="flex-start">
            {/* Sol Taraf - Başlık ve EYP Nedir */}
            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '2.8rem' },
                    color: '#1e293b',
                    mb: 3,
                    lineHeight: 1.2,
                  }}
                >
                  Resmi Yazışma Standardı:{' '}
                  <Box component="span" sx={{ color: '#0891b2', display: 'block' }}>
                    EYP
                  </Box>
                </Typography>

                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.05rem',
                      color: '#1e293b',
                      mb: 2,
                    }}
                  >
                    EYP Nedir?
                  </Typography>

                  <Typography
                    sx={{
                      color: P.neutral[600],
                      fontSize: '0.95rem',
                      lineHeight: 1.75,
                      mb: 2,
                    }}
                  >
                    EYP (Elektronik Yazışma Paketi), kamu kurumları arasında gerçekleştirilen
                    yazışmaların elektronik ortamda ve standart bir formatta hazırlanmasını
                    sağlayan resmi belge formatıdır.
                  </Typography>

                  {/* <Typography
                    sx={{
                      color: P.neutral[600],
                      fontSize: '0.95rem',
                      lineHeight: 1.75,
                    }}
                  >
                    Bu format, Cumhurbaşkanlığı Dijital Dönüşüm Ofisi tarafından belirlenen elektronik
                    yazışma standartları çerçevesinde tanımlanmıştır. Türkiye'de kamu kurum ve
                    kuruluşları arasındaki resmi yazışmalar bu standart doğrultusunda elektronik
                    ortamda yürütülmektedir.
                  </Typography> */}
                </Box>

                    <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    my: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: '#1e293b',
                      mb: 2,
                    }}
                  >
                    Hukuki Geçerlilik
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: P.neutral[600], lineHeight: 1.7, mb: 2 }}>
                    EYP formatında hazırlanan ve elektronik imza ile imzalanan belgeler,
                    5070 sayılı Elektronik İmza Kanunu kapsamında hukuki geçerliliğe sahip
                    resmi belgeler olarak kabul edilir.
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: P.neutral[600], lineHeight: 1.7 }}>
                    Böylece derneklerin kamu kurumları ile gerçekleştirdiği yazışmalar güvenli,
                    standartlara uygun ve hukuken geçerli bir şekilde yürütülür.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            {/* Sağ Taraf - Özellikler ve Detaylar */}
            <Grid item xs={12} md={6}>
              <motion.div
               style={{
                height:'100%',
                display:'flex',
                alignItems:'center',
                flexDirection:'column',
                justifyContent:'center'
               }}
              >
                <Box
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    background: alpha('#0891b2', 0.04),
                    borderLeft: '4px solid #0891b2',
                  }}
                >
                  <Typography
                    sx={{
                      color: P.neutral[700],
                      fontSize: '0.95rem',
                      lineHeight: 1.75,
                      fontWeight: 500,
                      mb: 2,
                    }}
                  >
                    {tenantName} Elektronik Belge Yönetim Sistemi'nin EYP uyumlu altyapısı sayesinde
                    resmi yazılar:
                  </Typography>

                  <Grid container spacing={2}>
                    {[
                      'Standart bir paket formatında hazırlanabilir',
                      'BTK lisanslı elektronik imza altyapısı ile imzalanabilir',
                      'Güvenli şekilde gönderilebilir',
                      'Belge içeriği, ekleri ve imza bilgileri tek bir paket içerisinde saklanabilir',
                    ].map((item, i) => (
                      <Grid item xs={12} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <CheckCircleIcon
                            sx={{
                              fontSize: 20,
                              color: '#0891b2',
                              mr: 1.5,
                              mt: 0.3,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: '0.9rem', color: P.neutral[600], lineHeight: 1.6 }}>
                            {item}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  Bu yapı sayesinde dernekler, kamu kurumları ile gerçekleştirdikleri yazışmaları
                  Cumhurbaşkanlığı Dijital Dönüşüm Ofisi tarafından belirlenen elektronik yazışma
                  standartlarına uygun şekilde hazırlayabilir ve gönderebilir.
                </Typography>

            

                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontSize: '1rem',
                      lineHeight: 1.75,
                      fontWeight: 500,
                    }}
                  >
                    {tenantName} EBYS sayesinde dernekler, kamu kurumlarının kullandığı resmi
                    elektronik yazışma standardı ile uyumlu şekilde hukuken geçerli yazışmalar
                    gerçekleştirebilir.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* AI Assistant Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 18 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <Box
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                  border: '1px solid #e9d5ff',
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #7408b2ff 0%, #ee22c9ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <img
                    src="/assets/icon/ai.webp"
                    alt="AI"
                    style={{
                      width: '60%',
                      height: '60%',
                      objectFit: 'contain',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    color: '#1e293b',
                    mb: 2,
                  }}
                >
                  Yapay Zekâ Destekli
                  <br />
                  Resmi Yazışma Asistanı
                </Typography>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    mb: 3,
                  }}
                >
                  Resmi yazışma hazırlamak çoğu kullanıcı için zor ve zaman alıcı olabilir. 
                  {tenantName} EBYS sistemi bu süreci kolaylaştırmak için yapay zekâ destekli 
                  yazı editörü sunar.
                </Typography>

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                  }}
                >
                  <Typography
                    sx={{
                      color: P.neutral[700],
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      fontStyle: 'italic',
                    }}
                  >
                    Kullanıcı yalnızca yazının içeriğini hazırlar.
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.3rem',
                  color: '#1e293b',
                  mb: 2,
                }}
              >
                {tenantName} yapay zekâ editörü ise metni analiz ederek:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
                {AI_FEATURES.map((feature, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 24, color: '#af08b2ff', flexShrink: 0, mt: 0.3 }}
                    />
                    <Typography sx={{ fontSize: '1rem', color: P.neutral[700], lineHeight: 1.6 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                  borderLeft: '1px solid #a908b268',
                }}
              >
                <Typography
                  sx={{
                    color: P.neutral[700],
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    fontWeight: 500,
                  }}
                >
                  Bu sayede resmi yazışma deneyimi olmayan kullanıcılar bile doğru, güçlü ve 
                  kurumsal belgeler hazırlayabilir.
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Editor Section */}
      <Box sx={{ background: '#f8fafc', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          {/* Ana Editör Bölümü */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.7rem', md: '2.5rem' },
                    color: '#1e293b',
                    mb: 2,
                  }}
                >
                  Gelişmiş Online Yazışma Editörü
                </Typography>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  {tenantName} EBYS sistemi içerisinde bulunan gelişmiş online belge editörü, 
                  Word veya başka bir program kullanmaya gerek bırakmaz.
                </Typography>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  Tüm yazışmalar doğrudan {tenantName} üzerinden oluşturulabilir, düzenlenebilir 
                  ve imzalanabilir.
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#1e293b',
                    fontSize: '1rem',
                    mb: 2,
                  }}
                >
                  Editörün sunduğu başlıca özellikler:
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  {EDITOR_FEATURES.map((feature, i) => (
                    <Box
                      key={i}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 22, color: '#0891b2', flexShrink: 0, mt: 0.3 }}
                      />
                      <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700], lineHeight: 1.6 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                    fontStyle: 'italic',
                  }}
                >
                  Bu sayede tüm yazışmalar tek platform üzerinden hızlı ve düzenli şekilde 
                  yönetilebilir.
                </Typography>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeUp}
                custom={1}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: '#ffffff',
                    border: `1px solid ${alpha('#0891b2', 0.15)}`,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color: '#1e293b',
                      mb: 2,
                    }}
                  >
                    Antet ve Yazı Şablonları
                  </Typography>

                  <Typography 
                    sx={{ 
                      fontSize: '0.95rem', 
                      color: P.neutral[600], 
                      lineHeight: 1.75,
                      mb: 2,
                    }}
                  >
                    {tenantName} EBYS sistemi ile derneğe özel kurumsal antetler kolayca 
                    oluşturulabilir.
                  </Typography>

                  <Typography 
                    sx={{ 
                      fontSize: '0.95rem', 
                      color: P.neutral[600], 
                      lineHeight: 1.75,
                      mb: 3,
                    }}
                  >
                    Sık kullanılan yazılar için yazı şablonları tanımlanabilir ve böylece 
                    her belgeyi yeniden hazırlama ihtiyacı ortadan kalkar.
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#1e293b',
                      fontSize: '0.95rem',
                      mb: 2,
                    }}
                  >
                    Bu özellik sayesinde:
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {TEMPLATE_FEATURES.map((feature, i) => (
                      <Box
                        key={i}
                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 20, color: '#0891b2', flexShrink: 0, mt: 0.2 }}
                        />
                        <Typography sx={{ fontSize: '0.9rem', color: P.neutral[600], lineHeight: 1.6 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                    {/* Kurum İçi Yazışma */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 4,
                     background: '#ffffff',
                    border: `1px solid ${alpha('#0891b2', 0.15)}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color: '#1e293b',
                      mb: 2,
                    }}
                  >
                    Kurum İçi Yazışma Altyapısı
                  </Typography>

                  <Typography 
                    sx={{ 
                      fontSize: '0.95rem', 
                      color: P.neutral[600], 
                      lineHeight: 1.75,
                      mb: 3,
                    }}
                  >
                    {tenantName} EBYS sistemi yalnızca dış yazışmalar için değil, kurum içi 
                    iletişim için de güçlü bir altyapı sunar.
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#1e293b',
                      fontSize: '0.95rem',
                      mb: 2,
                    }}
                  >
                    Özellikle:
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    {INSTITUTION_TYPES.map((type, i) => (
                      <Box
                        key={i}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 20, color: '#0891b2', flexShrink: 0 }}
                        />
                        <Typography sx={{ fontSize: '0.9rem', color: P.neutral[700], fontWeight: 500 }}>
                          {type}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography 
                    sx={{ 
                      fontSize: '0.95rem', 
                      color: P.neutral[600], 
                      lineHeight: 1.75,
                    }}
                  >
                    için geliştirilen modüler yapı sayesinde merkez ve şubeler arasında resmi 
                    kurum içi yazışmalar kolayca gerçekleştirilebilir.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                variants={fadeUp}
                custom={1}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.7rem', md: '2.5rem' },
                    color: '#1e293b',
                    mb: 2,
                  }}
                >
                  Gelişmiş Belge Yönetimi ve Arşiv
                </Typography>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  {tenantName} EBYS yalnızca bir yazı oluşturma aracı değildir; aynı zamanda 
                  güçlü bir belge yönetimi ve güvenli dijital arşiv altyapısı sunar.
                </Typography>

                <Typography
                  sx={{
                    color: P.neutral[600],
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                    mb: 3,
                  }}
                >
                  BTK lisanslı e-imza altyapısı sayesinde belgeler nitelikli elektronik imza 
                  (e-imza) ile 5070 sayılı Elektronik İmza Kanunu kapsamında imzalanabilir, 
                  zaman damgası ile güvence altına alınarak hukuki geçerlilik kazanır ve 
                  güvenli şekilde arşivlenebilir.
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#1e293b',
                    fontSize: '1rem',
                    mb: 2,
                  }}
                >
                  Başlıca özellikler:
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {ARCHIVE_FEATURES.map((feature, i) => (
                    <Box
                      key={i}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 22, color: '#0891b2', flexShrink: 0, mt: 0.3 }}
                      />
                      <Typography sx={{ fontSize: '0.95rem', color: P.neutral[700], lineHeight: 1.6 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
          
      </Box>

            {/* 7️⃣ KAPANIŞ CTA */}
      <Box
        sx={{
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              background: `linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              py: { xs: 6, md: 8 },
              px: { xs: 3, md: 6 },
            }}
          >
            {/* Arka plan deseni */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.15,
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}
            />

            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(50px)',
              }}
            />

            <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                Resmi Yazışmalarınızı Dijitale Taşıyın
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  mb: 5,
                  opacity: 0.95,
                  maxWidth: '640px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                BTK lisanslı e-imza, EYP uyumlu belge üretimi ve yapay zeka destekli editör ile 
                resmi yazışmalarınızı hızlı, güvenli ve mevzuata uygun şekilde yönetin.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  //onClick={() => navigate('/basvuru')}
                  sx={{
                    bgcolor: 'white',
                    color: '#0891b2',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: '#fafafa',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Hemen Başvurun
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  //onClick={() => navigate('/iletisim')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    borderWidth: 2,
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  İletişime Geçin
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default EBYSPage;
